import { useCallback, useEffect, useRef, useState } from "react";

/** How long every success / error / warning / info message stays visible. */
export const MESSAGE_DURATION_MS = 30000;

export function secondsUntil(endAt) {
  return Math.max(0, Math.ceil((endAt - Date.now()) / 1000));
}

/** Whole seconds left until ``endAt`` (ms timestamp), updated every second. */
export function useSecondsLeft(endAt, fallbackSeconds = Math.ceil(MESSAGE_DURATION_MS / 1000)) {
  const [left, setLeft] = useState(() => (endAt ? secondsUntil(endAt) : fallbackSeconds));
  useEffect(() => {
    if (!endAt) {
      setLeft(fallbackSeconds);
      return undefined;
    }
    setLeft(secondsUntil(endAt));
    const id = setInterval(() => setLeft(secondsUntil(endAt)), 1000);
    return () => clearInterval(id);
  }, [endAt, fallbackSeconds]);
  return left;
}

/**
 * A message closes ``durationMs`` after it appears, or earlier via ``close()``;
 * it then stays hidden until its content (``signature``) changes or clears.
 * Returns ``{ visible, secondsLeft, close }``.
 */
export default function useAutoHide(signature, { enabled = true, durationMs = MESSAGE_DURATION_MS, onExpire } = {}) {
  const sig = signature ? String(signature) : "";
  const [closed, setClosed] = useState(null);
  const [endAt, setEndAt] = useState(null);
  const onExpireRef = useRef(onExpire);
  onExpireRef.current = onExpire;

  useEffect(() => {
    if (!sig) {
      setClosed(null);
      setEndAt(null);
      return undefined;
    }
    if (!enabled || closed === sig) return undefined;
    setEndAt(Date.now() + durationMs);
    const timer = setTimeout(() => {
      setClosed(sig);
      onExpireRef.current?.();
    }, durationMs);
    return () => clearTimeout(timer);
  }, [sig, enabled, durationMs, closed]);

  const visible = !(enabled && sig && closed === sig);
  const secondsLeft = useSecondsLeft(visible && enabled ? endAt : null, Math.ceil(durationMs / 1000));
  const close = useCallback(() => setClosed(sig), [sig]);

  return { visible, secondsLeft, close };
}
